/**
 * Per-screen display settings (layout, grouping, ordering, filters), keyed like
 * `project:WAFL:issues:active`. Kept locally for instant UI and synced to the
 * server so they follow the user across devices.
 */
import { create } from "zustand";
import { api } from "./api";

export type Layout = "list" | "board";
export type GroupBy = "state" | "assignee" | "priority" | "label" | "task" | "none";
export type OrderBy = "manual" | "priority" | "updated" | "created" | "due";
export type Filters = {
  states?: string[];
  stateTypes?: string[];
  assignees?: (string | null)[];
  priorities?: number[];
  labels?: string[];
  tasks?: (string | null)[];
  text?: string;
};
export type ViewSettings = {
  milestoneDiagram?: { layout?: "diagram" | "list"; milestoneId?: string; status?: string; leadId?: string };
  layout: Layout;
  groupBy: GroupBy;
  orderBy: OrderBy;
  showCompleted: "none" | "week" | "month" | "all";
  showSubIssues: boolean;
  showEmptyGroups: boolean;
  props: { labels: boolean; assignee: boolean; due: boolean; created: boolean; estimate: boolean; id: boolean };
  filters: Filters;
};

export const DEFAULT_VIEW: ViewSettings = {
  layout: "list",
  groupBy: "state",
  orderBy: "manual",
  showCompleted: "week",
  showSubIssues: true,
  showEmptyGroups: false,
  props: { labels: true, assignee: true, due: true, created: true, estimate: true, id: true },
  filters: {},
};

type State = {
  views: Record<string, ViewSettings>;
  loaded: boolean;
  hydrate: () => Promise<void>;
  get: (key: string) => ViewSettings;
  set: (key: string, patch: Partial<ViewSettings>) => void;
  setFilters: (key: string, filters: Filters) => void;
};

const LS = "tern.views";
const timers = new Map<string, ReturnType<typeof setTimeout>>();

function readLocal(): Record<string, ViewSettings> {
  try {
    return JSON.parse(localStorage.getItem(LS) ?? "{}");
  } catch {
    return {};
  }
}

export const useViews = create<State>((set, get) => ({
  views: readLocal(),
  loaded: false,
  async hydrate() {
    if (get().loaded) return;
    try {
      const res = await api.GET("/api/me/views");
      if (res.data) {
        const server = Object.fromEntries(res.data.map((v) => [v.view_key, { ...DEFAULT_VIEW, ...(v.settings as Partial<ViewSettings>) }]));
        set((s) => ({ views: { ...s.views, ...server }, loaded: true }));
        try {
          localStorage.setItem(LS, JSON.stringify(get().views));
        } catch {
          /* ignore */
        }
      }
    } catch {
      set({ loaded: true });
    }
  },
  get(key) {
    return get().views[key] ?? DEFAULT_VIEW;
  },
  set(key, patch) {
    const next = { ...get().get(key), ...patch };
    set((s) => ({ views: { ...s.views, [key]: next } }));
    try {
      localStorage.setItem(LS, JSON.stringify(get().views));
    } catch {
      /* ignore */
    }
    clearTimeout(timers.get(key));
    timers.set(
      key,
      setTimeout(() => {
        void api.PUT("/api/me/views/{key}", { params: { path: { key } }, body: next as unknown as Record<string, never> });
      }, 800),
    );
  },
  setFilters(key, filters) {
    get().set(key, { filters });
  },
}));

export const useView = (key: string) => useViews((s) => s.views[key] ?? DEFAULT_VIEW);
